import { Box, Text, Checkbox } from "@chakra-ui/react";
import { Col, Modal, Row } from "antd";
import InputMoney from "components/Input/InputMoney/index";
import useSaveConsumo from "core/features/consumo/hooks/useSaveConsumo";
import {
  ConsumoDto,
  EditarConsumoForm,
} from "core/features/consumo/typings/index";
import { useForm } from "react-hook-form";
import FooterModal from "ui/components/Button/FooterModal";
import Field from "ui/components/Input/Field";
import moment from "moment";
import {
  mapUnidadeMedidaExtenso,
  FaixaConsumoTipoValorEnum,
} from "core/features/consumo/typings";
import FaixaConsumoItem from "../FaixaConsumoItem/index";
import OutlineButton from "ui/components/Button/OutlineButton";

interface ModalEditarConsumoProps {
  open: boolean;
  consumo: ConsumoDto;
  onClose: () => any;
  refetch?: () => any;
}

export default function ModalEditarConsumo({
  open,
  consumo,
  onClose,
  refetch,
}: ModalEditarConsumoProps) {
  const { control, handleSubmit, watch, setValue } = useForm<EditarConsumoForm>(
    {
      defaultValues: {
        diaMedicao: consumo.diaMedicao,
        valorUnidade: consumo.valorUnidade,
        faixas: consumo.faixas ?? [],
        replicarValorMesesFuturos: false,
      },
      mode: "onBlur",
    }
  );

  const { mutate: saveConsumo, isLoading } = useSaveConsumo(consumo.id);

  const onSubmit = (form: EditarConsumoForm) => {
    saveConsumo(form, {
      onSuccess: () => {
        onClose();
        refetch?.();
      },
    });
  };

  const faixas = watch("faixas");

  return (
    <Modal
      visible={open}
      destroyOnClose
      onCancel={onClose}
      width={"850px"}
      title={
        consumo.mes
          ? `Editar consumo de ${moment(consumo.mes, "YYYY-MM").format(
              "MMMM/YYYY"
            )}`
          : "Editar consumo"
      }
      footer={
        <FooterModal
          onClose={onClose}
          showRegisterAnother={false}
          onSubmit={handleSubmit(onSubmit)}
          isLoadingSave={isLoading}
        />
      }
    >
      <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
        <Box as={Row} mb={4} gutter={16}>
          <Col xs={12}>
            <Field
              label={
                consumo.unidadeMedida
                  ? `Valor por ${
                      mapUnidadeMedidaExtenso[consumo.unidadeMedida]
                    }`
                  : "Valor por medida"
              }
              control={control}
              name="valorUnidade"
              render={({ field }) => <InputMoney {...field} />}
            />
          </Col>
          <Col xs={12}>
            <Field
              label="Dia de medição"
              control={control}
              name="diaMedicao"
              render={({ field }) => (
                <InputMoney
                  {...field}
                  decimalScale={0}
                  decimalsLimit={0}
                  allowDecimals={false}
                  placeholder="1"
                />
              )}
            />
          </Col>
        </Box>

        <Box as={Row} mb={4} gutter={16}>
          <Col xs={12}>
            <Field
              control={control}
              name="replicarValorMesesFuturos"
              render={({ field }) => (
                <Checkbox
                  isChecked={!!field.value}
                  onChange={() => field.onChange(!field.value)}
                >
                  Replicar valor e dia para meses futuros
                </Checkbox>
              )}
            />
          </Col>
        </Box>

        <Box display="flex" alignItems="center" mt={4} mb={4}>
          <Text as="h3" mr={2} fontSize="16px" fontWeight="500">
            Faixas de consumo
          </Text>

          <OutlineButton
            type="button"
            onClick={() => {
              const lastFaixa = faixas?.[faixas.length - 1];

              setValue("faixas", [
                ...(faixas ?? []),
                {
                  tempId: Date.now().toString(),
                  inicioFaixa: lastFaixa ? lastFaixa.fimFaixa : 0,
                  fimFaixa: undefined,
                  valor: 0,
                  tipoValor: FaixaConsumoTipoValorEnum.UnidadeMedida,
                },
              ]);
            }}
          >
            Adicionar faixa
          </OutlineButton>
        </Box>

        {faixas?.map((faixa, index: number) => (
          <FaixaConsumoItem
            setValue={setValue}
            faixas={faixas}
            key={faixa.id ?? faixa.tempId}
            index={index}
            control={control}
            consumo={consumo}
          />
        ))}
      </form>
    </Modal>
  );
}
